import Modal from "@/Components/Modal";
import TextInput from "@/Components/TextInput";
import InputLabel from "@/Components/InputLabel";
import PrimaryButton from "@/Components/PrimaryButton";
import SecondaryButton from "@/Components/SecondaryButton";
import Checkbox from "@/Components/Checkbox";
import CloseIcon from "@/Components/Icons/CloseIcon";
import Popover from "@/Components/Popover";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEnvelope, faLanguage, faQuestionCircle} from "@fortawesome/free-solid-svg-icons";
import React, {useEffect, useMemo, useState} from "react";
import {v4 as uuidv4} from "uuid";
import {EmailNotificationStep, TranslationStep, Workflow, WorkflowStep} from "@/types/workflow";
import {SubtitleFormat} from "@/types";
import useSupportedLanguages from "@/hooks/useSupportedLanguages";
import {TranslationMethod} from "@/types/enums";
import {createOrUpdateWorkflow, createWorkflow, updateWorkflow} from "@/services/workflowService";
import Select from "@/Components/Select";
import AiTranslateInfoModal from "@/Pages/Dashboard/Translate/AiTranslateInfoModal";

export interface WorkflowFormModalProps {
    show: boolean;
    onClose: () => void;
    onSave: (workflow: Workflow) => void;
    workflow?: Workflow;
    isPaidUser: boolean;
    showDescription?: boolean;
}

interface FormData {
    name: string;
    translationEnabled: boolean;
    emailEnabled: boolean;
    translationConfig: {
        target_languages: string[];
    };
    emailConfig: {
        recipients: string[];
        recipientsText: string; // ADD THIS LINE - stores raw textarea content
        include_original: boolean;
        include_translated: boolean;
        subtitle_format: SubtitleFormat;
    };
    isDefault: boolean;
}

interface FormErrors {
    translation?: string;
    general?: string;
}

const SUBTITLE_FORMAT_OPTIONS = [
    { value: 'srt' as SubtitleFormat, label: 'SRT' },
    { value: 'vtt' as SubtitleFormat, label: 'VTT' },
    { value: 'txt' as SubtitleFormat, label: 'TXT' },
];

const DEFAULT_FORM_DATA: FormData = {
    name: '',
    translationEnabled: false,
    emailEnabled: false,
    translationConfig: {
        target_languages: []
    },
    emailConfig: {
        recipients: [],
        recipientsText: '',
        include_original: true,
        include_translated: true,
        subtitle_format: 'srt'
    },
    isDefault: false
}

export default function WorkflowFormModal(
    {
        show,
        onClose,
        onSave,
        workflow,
        isPaidUser,
        showDescription = true
    }: WorkflowFormModalProps
) {
    const [showTranslateInfo, setShowTranslateInfo] = useState(false);
    const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [languageOptions] = useSupportedLanguages('translation', TranslationMethod.AI);
    const isEditing = !!workflow;

    const selectedLanguages = useMemo(() => {
        return languageOptions.filter(opt => formData.translationConfig.target_languages.includes(opt.value));
    }, [languageOptions, formData.translationConfig.target_languages]);

    const selectedSubtitleFormat = useMemo(() => {
        return SUBTITLE_FORMAT_OPTIONS.find(opt => opt.value === formData.emailConfig.subtitle_format);
    }, [formData.emailConfig.subtitle_format]);

    useEffect(() => {
        if (show) {
            if (workflow) {
                // Ensure steps is an array
                const workflowSteps = Array.isArray(workflow.steps) ? workflow.steps : [];

                const translationStep = workflowSteps.find(step => step.type === 'translation') as TranslationStep;
                const emailStep = workflowSteps.find(step => step.type === 'email_notification') as EmailNotificationStep;

                setFormData({
                    name: workflow.name,
                    translationEnabled: !!translationStep,
                    emailEnabled: !!emailStep,
                    translationConfig: {
                        target_languages: translationStep?.config.target_languages || []
                    },
                    emailConfig: {
                        recipients: emailStep?.config.recipients || [],
                        recipientsText: emailStep?.config.recipients?.join('\n') || '',
                        include_original: emailStep?.config.include_original ?? true,
                        include_translated: emailStep?.config.include_translated ?? true,
                        subtitle_format: emailStep?.config.subtitle_format || 'srt'
                    },
                    isDefault: (workflow as any).isDefault || false
                });
            } else {
                setFormData(DEFAULT_FORM_DATA);
            }
            setErrors({});
            setIsSubmitting(false);
        }
    }, [show, workflow]);

    const clearError = (errorKey: keyof FormErrors) => {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            return newErrors;
        });
    };

    const constructWorkflow = (): Workflow => {
        const steps: WorkflowStep[] = [];

        if (formData.translationEnabled) {
            steps.push({
                type: 'translation',
                name: 'Translate subtitles',
                config: {
                    target_languages: formData.translationConfig.target_languages
                }
            } as TranslationStep);
        }

        if (formData.emailEnabled) {
            const recipients = formData.emailConfig.recipientsText
                .split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            steps.push({
                type: 'email_notification',
                name: 'Email notification',
                config: {
                    recipients,
                    include_original: formData.emailConfig.include_original,
                    include_translated: formData.emailConfig.include_translated,
                    subtitle_format: formData.emailConfig.subtitle_format
                }
            } as EmailNotificationStep);
        }

        return {
            id: workflow?.id || -1,
            name: formData.name.trim(),
            steps,
            isDefault: formData.isDefault
        };
    }

    const validateForm = (form: HTMLFormElement): boolean => {
        const inputs = form.querySelectorAll('input, textarea, select');
        inputs.forEach(input => (input as HTMLInputElement).setCustomValidity(''));

        let hasCustomErrors = false;

        if (!formData.translationEnabled && !formData.emailEnabled) {
            setErrors(prev => ({ ...prev, general: 'At least one workflow step must be enabled' }));
            hasCustomErrors = true;
        } else {
            setErrors(prev => ({ ...prev, general: undefined }));
        }

        if (formData.translationEnabled && formData.translationConfig.target_languages.length === 0) {
            setErrors(prev => ({ ...prev, translation: 'At least one target language is required' }));
            hasCustomErrors = true;
        } else {
            setErrors(prev => ({ ...prev, translation: undefined }));
        }

        if (formData.emailEnabled) {
            const recipients = formData.emailConfig.recipientsText
                .split('\n')
                .map(email => email.trim())
                .filter(email => email.length > 0);

            if (recipients.length === 0) {
                const recipientsTextarea = form.querySelector('textarea[name="recipients"]') as HTMLTextAreaElement;
                recipientsTextarea?.setCustomValidity('At least one recipient is required');
                hasCustomErrors = true;
            }
        }

        if (!form.checkValidity() || hasCustomErrors) {
            form.reportValidity();
            return false;
        }

        return true;
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        if(!validateForm(form)) {
            return;
        }

        try {
            setIsSubmitting(true);
            const workflow = constructWorkflow();
            const result = await createOrUpdateWorkflow(workflow);
            onSave(result);
        } catch (error: any) {
            setErrors(prev => ({ ...prev, general: error.message }));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal maxWidth="xl" show={show} onClose={onClose} blurBackground={true}>
            <AiTranslateInfoModal blurredBackground={false} show={showTranslateInfo} onClose={() => setShowTranslateInfo(false)}/>
            <div className="w-full">
                {/* Header */}
                <div className="flex items-center p-7 py-4 border-b dark:border-gray-700 mb-8">
                    <div className="flex-grow">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                            {workflow ? 'Edit Workflow' : 'Create Workflow'}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 dark:text-gray-500 bg-transparent hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
                    >
                        <CloseIcon />
                        <span className="sr-only">Close modal</span>
                    </button>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit}>
                    <div className="px-7 mb-5">
                        {
                            showDescription && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <svg className="w-5 h-5 text-blue-400 dark:text-blue-300 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">About Workflows</h3>
                                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                                Workflows let you automate tasks that run after subtitle extraction, like translating subtitles and emailing the final files.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                    <div className="space-y-4 mb-8">
                        <div>
                            <InputLabel className="mb-2 text-sm font-medium" htmlFor="workflow-name" value="Workflow Name" />
                            <TextInput
                                id="workflow-name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, name: e.target.value }));
                                    // Clear custom validity when user types
                                    e.target.setCustomValidity('');
                                }}
                                className="w-full"
                                placeholder="e.g. Translate to Spanish and email"
                            />
                            {/* Remove custom error display - browser will handle it */}
                        </div>
                    </div>

                    {/* Translation Step */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <label className="flex items-center cursor-pointer">
                                <Checkbox
                                    checked={formData.translationEnabled}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        setFormData(prev => ({ ...prev, translationEnabled: newValue }));

                                        // Clear any "no steps enabled" validation error
                                        if (newValue || formData.emailEnabled) {
                                            clearError('general');
                                        }

                                        // Clear translation error if disabling
                                        if (!newValue) {
                                            clearError('translation');
                                        }
                                    }}
                                    name="translationEnabled"
                                />
                                <FontAwesomeIcon icon={faLanguage} className="text-gray-500 dark:text-gray-400 ml-3 mr-2" />
                                <span className="text-lg font-medium text-gray-900 dark:text-gray-100">Translate subtitles</span>
                            </label>
                        </div>

                        {formData.translationEnabled && (
                            <div>
                                <div className="ml-8 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <div>
                                        <InputLabel value="Target Languages" />
                                        <Select
                                            variant="modal"
                                            isMulti
                                            value={selectedLanguages}
                                            onChange={(selectedOptions) => {
                                                const languages = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                                setFormData(prev => ({
                                                    ...prev,
                                                    translationConfig: { ...prev.translationConfig, target_languages: languages }
                                                }));
                                                // Clear translation error if languages are selected
                                                if (languages.length > 0) {
                                                    clearError('translation');
                                                }
                                            }}
                                            menuPortalTarget={document ? document.body : undefined}
                                            styles={{
                                                input: (base) => ({...base, "input:focus": { boxShadow: "none"} })
                                            }}
                                            isClearable={false}
                                            options={languageOptions}
                                            placeholder="Select target languages..."
                                            className="mt-1"
                                        />
                                        {errors.translation && <div className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.translation}</div>}
                                    </div>

                                </div>
                                <div className="ml-7 p-4 text-sm text-gray-600 dark:text-gray-400">
                                    <div>
                                        <span>Translations are powered by AI, and priced based on the duration of the video.</span>
                                    </div>
                                    <span
                                        className="inline-block  text-blue-700 dark:text-blue-400 hover:underline focus:outline-none cursor-pointer"
                                        onClick={() => setShowTranslateInfo(true)}>
                                    See Details
                                </span>
                                </div>

                            </div>
                        )}
                    </div>

                    {/* Email Step */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <label className="flex items-center cursor-pointer">
                                <Checkbox
                                    checked={formData.emailEnabled}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        setFormData(prev => ({ ...prev, emailEnabled: newValue }));

                                        if (formData.translationEnabled || newValue) {
                                            clearError('general');
                                        }

                                        if (!newValue) {
                                            const form = document.querySelector('form');
                                            const recipientsTextarea = form?.querySelector('textarea[name="recipients"]') as HTMLTextAreaElement;
                                            recipientsTextarea?.setCustomValidity('');
                                        }
                                    }}
                                    name="emailEnabled"
                                />
                                <FontAwesomeIcon icon={faEnvelope} className="text-gray-500 dark:text-gray-400 ml-3 mr-2" />
                                <span className="text-lg font-medium text-gray-900 dark:text-gray-100">Email notification</span>
                            </label>
                        </div>

                        {formData.emailEnabled && (
                            <div className="ml-8 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-4">
                                <div>
                                    <InputLabel value="Email Recipients (one per line)" />
                                    <textarea
                                        name="recipients"
                                        value={formData.emailConfig.recipientsText}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                emailConfig: {
                                                    ...prev.emailConfig,
                                                    recipientsText: e.target.value
                                                }
                                            }));
                                            e.target.setCustomValidity('');
                                        }}
                                        className="w-full mt-1 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                        rows={3}
                                        placeholder="user@example.com"
                                    />
                                </div>

                                <div>
                                    <InputLabel value="Subtitle Format" />
                                    <Select
                                        variant="modal"
                                        value={selectedSubtitleFormat}
                                        onChange={(option) => {
                                            if (option) {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    emailConfig: { ...prev.emailConfig, subtitle_format: option.value }
                                                }));
                                            }
                                        }}
                                        menuPortalTarget={document ? document.body : undefined}
                                        isSearchable={false}
                                        options={SUBTITLE_FORMAT_OPTIONS}
                                        className="mt-1"
                                    />
                                </div>

                                {
                                    formData.translationEnabled && (
                                        <div className="flex items-center space-x-6">
                                            <label className="flex items-center">
                                                <Checkbox
                                                    checked={formData.emailConfig.include_original}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        emailConfig: { ...prev.emailConfig, include_original: e.target.checked }
                                                    }))}
                                                />
                                                <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Include original subtitles</span>
                                            </label>
                                            <label className="flex items-center">
                                                <Checkbox
                                                    checked={formData.emailConfig.include_translated}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        emailConfig: { ...prev.emailConfig, include_translated: e.target.checked }
                                                    }))}
                                                />
                                                <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Include translated subtitles</span>
                                            </label>
                                        </div>
                                    )
                                }
                            </div>
                        )}
                    </div>

                    {/* General Error */}
                    {errors.general && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <svg className="w-5 h-5 text-red-400 dark:text-red-300" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-800 dark:text-red-200">{errors.general}</p>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
                <div className="flex items-center justify-between pt-5 border-t border-gray-200 dark:border-gray-700 px-7 mb-5">
                    {/* Set Default Option */}
                    <div className="flex items-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.isDefault}
                                onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300"><span className="hidden md:inline">Set </span>default</span>
                        </label>
                        <Popover
                            delay={200}
                            trigger="hover"
                            placement="top"
                            content={
                                <div className="w-64 text-sm text-gray-500 dark:text-gray-100">
                                    <div className="px-3 py-2">
                                        <p>This workflow will be pre-selected when creating new extractions.</p>
                                    </div>
                                </div>
                            }
                        >
                            <FontAwesomeIcon
                                icon={faQuestionCircle}
                                className="ml-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                                size="sm"
                            />
                        </Popover>
                    </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-2">
                            <SecondaryButton type="button" onClick={onClose} className="hidden md:inline-flex">
                                Cancel
                            </SecondaryButton>

                            <Popover
                                disabled={isPaidUser}
                                trigger="hover"
                                placement="left-start"
                                content={
                                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                        <p>Workflows are not available in the free trial.</p>
                                    </div>
                                    }
                                >
                                <span className="inline-block pl-1">
                                    <PrimaryButton
                                        type="submit"
                                        className={`${!isPaidUser ? 'cursor-not-allowed' : ''}`}
                                        disabled={isSubmitting || !isPaidUser}
                                    >
                                    <span>{isSubmitting ? 'Saving...' : (workflow ? 'Update' : 'Create')}</span>
                                </PrimaryButton>
                                </span>
                            </Popover>

                        </div>
                    </div>
                </form>
            </div>
        </Modal>
    );
}
